/**
 * @fileoverview Candidate Form Component - Multi-step candidate registration form
 * 
 * This component provides a comprehensive candidate registration system featuring:
 * - 9-step progressive form with stepper navigation
 * - Real-time form validation and data persistence
 * - Modular component architecture for maintainability
 * - Material-UI integration for consistent design
 * - State management for complex form data structures
 * 
 * Form Steps:
 * 1. Personal Information - Basic candidate details
 * 2. Professional Information - Skills and languages
 * 3. Education Details - Academic background
 * 4. Specialization - Professional specializations
 * 5. Work Experience - Employment history
 * 6. Resume & Portfolio - Document uploads
 * 7. Job Preferences - Location and role preferences
 * 8. Compliance & Verification - Document verification
 * 9. Additional Information - Personal interests and hobbies
 * 
 * @component
 * @author HireVeda Development Team
 * @version 2.0.0
 * @since 1.0.0
 */

// React Core
import React, { useState } from 'react';

// Material-UI Components
import { Stepper, Step, StepLabel, Button, Typography, Card } from '@mui/material';

// Form Step Components
import PersonalInformation from './components/PersonalInformation';
import ProfessionalInformation from './components/ProfessionalInformation';
import EducationDetails from './components/EducationDetails';
import Specialization from './components/Specialization';
import WorkExperience from './components/WorkExperience';
import ResumePortfolio from './components/ResumePortfolio';
import JobPreferences from './components/JobPreferences';
import ComplianceVerification from './components/ComplianceVerification';
import AdditionalInformation from './components/AdditionalInformation';

/**
 * Form Step Configuration
 * 
 * @constant {string[]} steps - Array of step labels for the stepper component
 * @description Defines the sequential steps in the candidate registration process
 */
const steps = [
  'Personal Information',
  'Professional Information',
  'Education Details',
  'Specialization',
  'Work Experience',
  'Resume & Portfolio',
  'Job Preferences',
  'Compliance & Verification',
  'Additional Information'
];

/**
 * CandidateForm Component
 * 
 * @description Main component that orchestrates the multi-step candidate registration form
 * @returns {JSX.Element} The complete candidate form with stepper navigation
 * 
 * @example
 * <CandidateForm />
 * 
 * @features
 * - Step-by-step form navigation
 * - Form data persistence across steps
 * - Array initialization for multi-select fields
 * - Responsive design with Material-UI
 */
const CandidateForm = () => {
  // State Management
  const [activeStep, setActiveStep] = useState(0);
  
  /**
   * Form Data State
   * 
   * @description Centralized state for all form data with proper array initialization
   * for multi-select fields to prevent undefined/null errors
   * 
   * @type {Object} formData - Complete form data structure
   * @property {Object} professionalInformation - Skills and language arrays
   * @property {Object} educationDetails - Degrees array
   * @property {Object} specialization - Specializations array
   * @property {Object} workExperience - Experiences array
   * @property {Object} jobPreferences - Preferred locations array
   * @property {Object} complianceVerification - Documents array
   * @property {Object} additionalInformation - Hobbies array
   */
  const [formData, setFormData] = useState({
    // Ensure all multiple select fields are initialized as arrays
    professionalInformation: {
      skills: [],
      languages: []
    },
    educationDetails: {
      degrees: []
    },
    specialization: {
      specializations: []
    },
    workExperience: {
      experiences: []
    },
    jobPreferences: {
      preferredLocations: []
    },
    complianceVerification: {
      documents: []
    },
    additionalInformation: {
      hobbies: []
    }
  });

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = () => {
    // Submit form data to the server
    console.log('Form Data:', formData);
    // axios.post('/api/submit', formData)
    //   .then(response => console.log(response))
    //   .catch(error => console.error(error));
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <PersonalInformation formData={formData} setFormData={setFormData} />;
      case 1:
        return <ProfessionalInformation formData={formData} setFormData={setFormData} />;
      case 2:
        return <EducationDetails formData={formData} setFormData={setFormData} />;
      case 3:
        return <Specialization formData={formData} setFormData={setFormData} />;
      case 4:
        return <WorkExperience formData={formData} setFormData={setFormData} />;
      case 5:
        return <ResumePortfolio formData={formData} setFormData={setFormData} />;
      case 6:
        return <JobPreferences formData={formData} setFormData={setFormData} />;
      case 7:
        return <ComplianceVerification formData={formData} setFormData={setFormData} />;
      case 8:
        return <AdditionalInformation formData={formData} setFormData={setFormData} />;
      default:
        throw new Error('Unknown step');
    }
  };

  return (
    <div>
      
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Card style={{ padding: 20, margin: 20, width: '50%', marginLeft: 'auto', marginRight: 'auto' }}>
      <>
        {activeStep === steps.length ? (
          <>
            <Typography>All steps completed</Typography>
            <Button onClick={handleSubmit}>Submit</Button>
          </>
        ) : (
          <>
            {getStepContent(activeStep)}
            <Button disabled={activeStep === 0} onClick={handleBack}>
              Back
            </Button>
            <Button variant="contained" color="primary" onClick={handleNext}>
              {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
            </Button>
          </>
        )}
      </>
      </Card>
    </div>
  );
};

export default CandidateForm;